/* Katha Bundle Studio — photo preview that recolours each piece of one real bed photo.
   The photo is the bed in undyed linen; each piece has its own mask (packed 3 to a PNG:
   a = duvet fitted flat, b = back pillowcases, front pillowcases, bolster, c = Euro shams).
   Colouring keeps the photo's own light and creases: every pixel keeps its brightness
   relative to the piece's typical tone, and that typical tone gets the exact swatch colour.
   The photo comes in four versions, with and without the Euro shams and the bolster, so add-ons only
   appear when they are added: img (both), bare (no shams), nobol (no bolster), barenobol (neither).
   One photo set per fabric (cfg.fabrics = {Linen: {...}, Eucalyptus: {...}}), each loaded the first time it is shown.
   A fabric photo without add-on versions ("addons": false) falls back to the Linen set while an add-on is on.
   cfg.spin = [12 views round the bed] (3D model renders, no add-ons): set({spin: i}) shows view i. */
(function(){
  const ORDER = ['duvet', 'fitted', 'flat', 'back', 'front', 'bolster', 'shams'];
  const SHEETS = {duvet:1, fitted:1, flat:1};
  const PERIOD_CM = {'pinstripe':1.25, 'pencilstripe':1, 'jetstripes':2, 'cocoa stripes':1.666};
  const LIN = new Float32Array(256).map((_, i) => { const c = i/255; return c <= .04045 ? c/12.92 : Math.pow((c + .055)/1.055, 2.4); });
  const SRGB = new Uint8ClampedArray(4096).map((_, i) => { const c = i/4095; return 255*(c <= .0031308 ? c*12.92 : 1.055*Math.pow(c, 1/2.4) - .055); });
  const toByte = v => SRGB[Math.min(4095, Math.max(0, v*4095 | 0))];
  const hexLin = h => { h = String(h || '#F2EFE9').replace('#', ''); return [0, 2, 4].map(i => LIN[parseInt(h.slice(i, i + 2), 16) || 0]); };

  function loadImg(src){
    return new Promise((ok, no) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = no; i.src = src; });
  }
  function pixels(img){
    const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
    const g = c.getContext('2d', {willReadFrequently:true}); g.drawImage(img, 0, 0);
    return g.getImageData(0, 0, c.width, c.height).data;
  }

  /* mount(viewportEl, cfg) -> {set({duvet, fitted, flat, back, front, bolster, shams}), pieceAt(event)}
     each value {hex, stripe?, w?, name?}; bolster/shams null = not added */
  window.KathaBedRecolour = function mount(vp, cfg){
    const FAB = cfg.fabrics || {Linen: cfg};
    const canvas = document.createElement('canvas');
    canvas.className = 'kbr-canvas';
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;object-fit:contain;max-width:none';
    vp.insertBefore(canvas, vp.firstChild);
    vp.style.background = cfg.bg || '#E4E4E4'; /* the photo's own wall, so the letterbox doesn't show */
    const ctx = canvas.getContext('2d', {willReadFrequently:true});
    let V = null, want = null, raf = 0;
    const cache = {};

    function load(vc){
      if (cache[vc.img]) return cache[vc.img];
      const full = vc.addons !== false;
      const urls = full ? [vc.img, vc.bare, vc.nobol || vc.img, vc.barenobol || vc.bare, vc.a, vc.b, vc.c] : [vc.img, vc.img, vc.img, vc.img, vc.a, vc.b, vc.c];
      return cache[vc.img] = Promise.all(urls.map(loadImg)).then(([ph, bare, nobol, barenobol, ma, mb, mc]) => {
      const w = ph.naturalWidth, h = ph.naturalHeight, n = w*h;
      const photo = pixels(ph), M = [pixels(ma), pixels(mb), pixels(mc)];
      const photos = full ? {11: photo, '01': pixels(bare), 10: pixels(nobol), '00': pixels(barenobol)} : {11: photo, '01': photo, 10: photo, '00': photo}; /* key: shams, bolster */
      /* brightness per pixel for each photo version: shading always comes from the photo on screen */
      const lumOf = d => { const Y = new Float32Array(n); for (let i = 0; i < n; i++) Y[i] = .2126*LIN[d[4*i]] + .7152*LIN[d[4*i + 1]] + .0722*LIN[d[4*i + 2]]; return Y; };
      const Ys = {}; if (full) for (const k in photos) Ys[k] = lumOf(photos[k]); else { const y0 = lumOf(photo); for (const k in photos) Ys[k] = y0; }
      /* fold field: the photo's soft shading, so stripes sway with the folds and puff instead of staying ruler-straight */
      const warp = (function(){
        const src = Ys['00'], a = new Float32Array(n), b = new Float32Array(n), r = 16;
        for (let y = 0; y < h; y++){ let acc = 0; const o = y*w;
          for (let x = -r; x < w; x++){ if (x + r < w) acc += src[o + x + r]; if (x - r - 1 >= 0) acc -= src[o + x - r - 1]; if (x >= 0) a[o + x] = acc/(2*r + 1); } }
        for (let x = 0; x < w; x++){ let acc = 0;
          for (let y = -r; y < h; y++){ if (y + r < h) acc += a[(y + r)*w + x]; if (y - r - 1 >= 0) acc -= a[(y - r - 1)*w + x]; if (y >= 0) b[y*w + x] = acc/(2*r + 1); } }
        return b;
      })();
      const px = {}, al = {}, yref = {};
      ORDER.forEach((k, ci) => {
        /* each piece's typical tone, from the version where nothing covers it */
        const src = M[Math.floor(ci/3)], ch = ci % 3, idx = [], alpha = [], solid = [], Y = Ys[(k === 'bolster' || k === 'shams') ? 11 : '00'];
        for (let i = 0; i < n; i++){ const a = src[4*i + ch]; if (a > 4){ idx.push(i); alpha.push(a); if (a > 240) solid.push(Y[i]); } }
        solid.sort((x, y) => x - y);
        px[k] = Int32Array.from(idx); al[k] = Uint8Array.from(alpha);
        yref[k] = solid[Math.floor(solid.length*.6)] || .7;
      });
      /* the main pieces' alpha total per pixel, never under 255: where masks overlap, their shares still make 100% */
      const bsum = new Float32Array(n).fill(255), tot = new Float32Array(n);
      ORDER.slice(0, 5).forEach(k => { const idx = px[k], a = al[k]; for (let j = 0; j < idx.length; j++) tot[idx[j]] += a[j]; });
      for (let i = 0; i < n; i++) if (tot[i] > 255) bsum[i] = tot[i];
      return {vc, w, h, photos, Ys, warp, px, al, yref, bsum, out: new ImageData(w, h)};
      });
    }
    /* the photo set for what is wanted: the fabric's own, or Linen's while an add-on it lacks is on */
    function viewFor(o){
      if (o && o.spin != null && cfg.spin && cfg.spin.length) return cfg.spin[((o.spin % cfg.spin.length) + cfg.spin.length) % cfg.spin.length];
      const own = FAB[o && o.fabric] || FAB.Linen || Object.values(FAB)[0];
      return (own.addons === false && (o.shams || o.bolster) && FAB.Linen) ? FAB.Linen : own;
    }
    function show(o){
      const vc = viewFor(o);
      if (o && o.spin != null && cfg.spin){   /* warm up the neighbours so turning feels instant */
        const n = cfg.spin.length, i = ((o.spin % n) + n) % n;
        [1, -1].forEach(d => load(cfg.spin[(i + d + n) % n]).catch(() => {}));
      }
      return load(vc).then(v => { if (viewFor(want) !== vc) return; if (V !== v){ V = v; canvas.width = v.w; canvas.height = v.h; } draw(); }).catch(() => {});
    }
    const ready = load(viewFor({fabric: 'Linen'}));

    function stripeAt(k, x, y, periodCm, wFrac, bend){
      let phase, pxw; const w = V.w, h = V.h;
      if (SHEETS[k] && V.vc.vp){
        /* sheets: stripes run along the bed towards the vanishing point of its long edges */
        const vc = V.vc, [vx, vy] = vc.vp || [w/4, -h/4], [[ax, ay], [bx, by]] = vc.foot || [[w*.15, h*.9], [w, h*.9]];
        const t0 = Math.atan2(ay - vy, ax - vx), t1 = Math.atan2(by - vy, bx - vx);
        const per = (t1 - t0)/(160/periodCm);
        phase = (Math.atan2(y - vy, x - vx) - t0)/per;
        pxw = 1/(Math.abs(per)*Math.hypot(x - vx, y - vy));
      } else {
        const per = (V.vc.pxPerCm || 4)*periodCm; phase = x/per; pxw = 1/per;
      }
      phase += bend; /* sway with the folds */
      const d = Math.abs(phase - Math.floor(phase) - .5), half = Math.max(wFrac, pxw*1.2)/2;
      const t = Math.min(1, Math.max(0, (half + pxw/2 - d)/pxw));
      return t*Math.min(1, wFrac/(2*half) + .25);
    }
    function render(){
      if (!V || !want) return;
      const key = (want.shams ? '1' : '0') + (want.bolster ? '1' : '0'), w = V.w, out = V.out.data, Y = V.Ys[key];
      const P = V.photos[key];
      out.set(P);
      for (const k of ORDER){
        /* the main pieces share their edge pixels (say 28% duvet + 72% flat): each adds its share of colour in place
           of the photo, so the shares make 100% and no undyed photo shows through as a pale seam.
           Add-ons (bolster, shams) lie on top of the pillows, so they are laid over instead. */
        const over = k === 'bolster' || k === 'shams';
        const col = want[k];
        if (!col) continue; /* add-on not added (its photo has it removed), or not chosen: the photo's own undyed linen */
        const base = hexLin(col.hex), str = col.stripe ? hexLin(col.stripe) : null;
        const period = col.period || PERIOD_CM[String(col.name || '').toLowerCase()] || 1.25;
        const wFrac = Math.min(.6, Math.max(.06, col.w || .1));
        /* how far stripes sway: a gentle drape, a fraction of a stripe */
        const sway = (SHEETS[k] ? 1.6 : 1.4)/period;
        const idx = V.px[k], al = V.al[k], yr = V.yref[k], boost = want.flash === k ? 1.13 : 1; /* a short lift on the piece just chosen */
        for (let j = 0; j < idx.length; j++){
          const i = idx[j], s = Y[i]/yr*boost, a = over ? al[j]/255 : al[j]/V.bsum[i], o = 4*i;
          let r = base[0], g = base[1], b = base[2];
          if (str){ const m = stripeAt(k, i % w, (i/w) | 0, period, wFrac, (V.warp[i]/yr - 1)*sway); r += (str[0] - r)*m; g += (str[1] - g)*m; b += (str[2] - b)*m; }
          if (over){
            out[o]     = out[o]*(1 - a)     + toByte(r*s)*a;
            out[o + 1] = out[o + 1]*(1 - a) + toByte(g*s)*a;
            out[o + 2] = out[o + 2]*(1 - a) + toByte(b*s)*a;
          } else {
            out[o]     += (toByte(r*s) - P[o])*a;
            out[o + 1] += (toByte(g*s) - P[o + 1])*a;
            out[o + 2] += (toByte(b*s) - P[o + 2])*a;
          }
        }
      }
      ctx.putImageData(V.out, 0, 0);
    }
    function draw(){ cancelAnimationFrame(raf); raf = requestAnimationFrame(render); }

    /* which piece is under a pointer event, or null */
    function pieceAt(e){
      if (!V) return null;
      const r = canvas.getBoundingClientRect();
      const sc = Math.min(r.width/V.w, r.height/V.h), ox = r.left + (r.width - V.w*sc)/2, oy = r.top + (r.height - V.h*sc)/2;
      const x = Math.floor((e.clientX - ox)/sc), y = Math.floor((e.clientY - oy)/sc);
      if (x < 0 || y < 0 || x >= V.w || y >= V.h) return null;
      const i = y*V.w + x; let best = null, ba = 100;
      for (const k of ORDER){
        if ((k === 'shams' || k === 'bolster') && !(want && want[k])) continue;
        const idx = V.px[k]; let lo = 0, hi = idx.length - 1;
        while (lo <= hi){ const m = (lo + hi) >> 1; if (idx[m] < i) lo = m + 1; else if (idx[m] > i) hi = m - 1; else { if (V.al[k][m] > ba){ ba = V.al[k][m]; best = k; } break; } }
      }
      return best;
    }
    /* where to pin a label for each piece: its right-hand edge, in px from the canvas's top-left */
    function anchors(){
      if (!V) return null;
      if (!V.anc){
        V.anc = {};
        for (const k of ORDER){
          const idx = V.px[k], al = V.al[k]; let xs = [];
          for (let j = 0; j < idx.length; j += 3) if (al[j] > 200) xs.push(idx[j] % V.w);
          if (!xs.length) continue;
          xs.sort((a, b) => a - b); const xr = xs[Math.floor(xs.length*.97)];
          let sy = 0, n = 0;
          for (let j = 0; j < idx.length; j += 3) if (al[j] > 200 && idx[j] % V.w >= xr - 6){ sy += (idx[j]/V.w) | 0; n++; }
          V.anc[k] = [xr, n ? sy/n : 0];
        }
      }
      const r = canvas.getBoundingClientRect(), sc = Math.min(r.width/V.w, r.height/V.h), ox = (r.width - V.w*sc)/2, oy = (r.height - V.h*sc)/2, o = {};
      for (const k in V.anc) o[k] = [ox + V.anc[k][0]*sc, oy + V.anc[k][1]*sc];
      return o;
    }
    return { canvas, ready, recolour:true, pieceAt, anchors, set(o){ want = o; return show(o); } };
  };
})();
